import {
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    colors,
    Grid,
    MenuItem,
    TextField,
    Theme,
    Typography
} from '@material-ui/core';
import {makeStyles} from '@material-ui/styles';
import React, {useState} from 'react';
import {useSnackbar} from 'notistack';
import clsx from 'clsx';
import {AutomaticPayment, AutomaticPaymentFrequencies} from '../../../graphql/payments.datatypes';
import {PaymentType} from '../../../graphql/payments.datatypes';
import {T} from 'react-polyglot-hooks';
import updateAutomaticPayment, {UpdateAutomaticPaymentInput} from './updateAutomaticPayment.api';
import deleteAutomaticPayment from './deleteAutomaticPayment.api';
import {getHandleFieldChange} from '../../../utils/formHelpers';
import fetchAutomaticPayment from '../fetchAutomaticPayment.api';
import { LoadingOverlay } from '../../../components/LoadingOverlay/LoadingOverlay';
import PaymentScheduleDetails from '../../PaymentSchedule/PaymentScheduleDetails';
import ConfirmDelete from '../../../components/ConfirmDelete';
import { BillingPartyMember } from "../../../graphql/billing_party.datatypes";
import getBillingParty from '../../BillingParty/getBillingParty.api';

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        marginTop: theme.spacing(3),
        width: '100%',
        marginBottom: theme.spacing(2)
    },
    card: {},
    submitButton: {
        color: theme.palette.white,
        backgroundColor: colors.green[600],
        '&:hover': {
            backgroundColor: colors.green[900]
        }
    },
    deleteButton: {
        marginLeft: theme.spacing(1),
        color: theme.palette.white,
        backgroundColor: theme.palette.error.light,
        '&:hover': {
            backgroundColor: theme.palette.error.main
        }
    },
    textField: {
        width: '100%'
    },
    dropdown: {
        marginBottom: theme.spacing(2)
    },
    actions: {
        justifyContent: 'flex-end'
    }
}));

const ManageAutomaticPayment = (props: {
    className?: string;
    onClose: () => void;
    onPaymentDelete: () => void;
    billingPartyMember: BillingPartyMember;
    automaticPayment: AutomaticPayment;
}) => {
    const {className, onClose, automaticPayment, onPaymentDelete, billingPartyMember} = props;
    const classes = useStyles();
    const [sendUpdateAutomaticPayment, updateData] = updateAutomaticPayment();
    const automaticPaymentData = fetchAutomaticPayment(automaticPayment.id);
    const [sendDeleteAutomaticPayment, deleteData] = deleteAutomaticPayment();
    const {enqueueSnackbar} = useSnackbar();
    const [formState, setFormState] = useState({
        id: automaticPayment.id,
        payment_frequency: automaticPayment.payment_frequency
    });
    const handleFieldChange = getHandleFieldChange(setFormState);
    const paymentType =
        automaticPayment.payment_type === PaymentType.CARD
            ? 'Credit Card'
            : 'E-Check';
    const [openAutoPaymentConfirmation, setOpenAutoPaymentConfirmation] = useState(false)

    const bpmBillingId = billingPartyMember.billing_party_id as number|null;
    const billingPartyData = getBillingParty(bpmBillingId);

    const onSaveClick = () => {
        const sanitisedData: UpdateAutomaticPaymentInput = {
            data: {
                id: formState.id,
                payment_frequency: formState.payment_frequency!,
            }
        };
        sendUpdateAutomaticPayment({variables: sanitisedData})
            .then(() => {
                enqueueSnackbar('Automatic Payment Updated.', {variant: 'success'});
                onClose()
            })
            .catch(() => {
                enqueueSnackbar('Automatic Payment Update Failed!', {variant: 'error'});
                onClose()
            });
    };

    const onPaymentDeleted = () => {
        setOpenAutoPaymentConfirmation(true)
    };

    const handleConfirmationClose = () => {
        setOpenAutoPaymentConfirmation(false)
    };

    const handlePaymentDelete = () => {
        setOpenAutoPaymentConfirmation(false)
        sendDeleteAutomaticPayment({variables: {id: automaticPayment.id}})
            .then(() => {
                enqueueSnackbar('Automatic Payment Deleted.', {variant: 'success'});
                onPaymentDelete()
                onClose()
            })
            .catch(() => {
                enqueueSnackbar('Automatic Payment Delete Failed!', {variant: 'error'});
                onClose()
            });
    };

    return (
        <Card className={clsx([className, classes.container])}>
            <CardHeader title="Automatic Payment Management"/>
            <CardContent className={classes.card}>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <TextField
                            select
                            className={classes.dropdown}
                            fullWidth
                            label="Payment Frequency"
                            value={formState.payment_frequency}
                            onChange={handleFieldChange}
                            variant="outlined"
                            margin="dense"
                            name="payment_frequency"
                            SelectProps={{
                                native: false
                            }}
                        >
                            {AutomaticPaymentFrequencies.map((paymentFrequency) => (
                                <MenuItem key={paymentFrequency.value} value={paymentFrequency.value}>
                                    {paymentFrequency.description}
                                </MenuItem>
                            ))}
                        </TextField>
                        <LoadingOverlay loading={automaticPaymentData.loading && billingPartyData.loading}>
                            <Grid item xs={12}>
                                {
                                    automaticPaymentData.data &&
                                    automaticPaymentData.data.automaticPayment &&
                                    automaticPaymentData.data.automaticPayment.paymentSchedule &&
                                    billingPartyData.data &&
                                    billingPartyData.data.billingParty && (
                                        <PaymentScheduleDetails
                                            billingParty={billingPartyData.data.billingParty}
                                            paymentSchedule={automaticPaymentData.data.automaticPayment.paymentSchedule}
                                        />
                                )}
                            </Grid>
                        </LoadingOverlay>
                    </Grid>
                </Grid>
                <Grid container spacing={1} style={{marginTop: '4vh'}}>
                    <Grid item xs={3}>
                        <Typography variant="h6">Payment Type</Typography>
                        <Typography variant="body1">{paymentType}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <Typography variant="h6">Last 4 of Account Number</Typography>
                        <Typography variant="body1">
                            {automaticPayment.last_four}
                        </Typography>
                    </Grid>
                    {automaticPayment.payment_type === PaymentType.ECHEQUE && (
                        <Grid item xs={3}>
                            <Typography variant="h6">Routing Number</Typography>
                            <Typography variant="body1">
                                {automaticPayment.routing_number ? automaticPayment.routing_number : 'N/A'}
                            </Typography>
                        </Grid>
                    )}
                    {automaticPayment.payment_type !== PaymentType.ECHEQUE && (
                        <Grid item xs={3} />
                    )}
                    <Grid item xs={2}>
                        <Button
                            className={classes.deleteButton}
                            variant="contained"
                            onClick={onPaymentDeleted}
                            style={{float: 'right'}}
                        >
                            <T phrase="Delete"/>
                        </Button>
                    </Grid>
                </Grid>
            </CardContent>
            <CardActions className={classes.actions}>
                <Button
                    onClick={onSaveClick}
                    variant="contained"
                    className={classes.submitButton}>
                    <T phrase="Submit"/>
                </Button>
                <Button variant={'outlined'} onClick={onClose}>
                    <T phrase="Cancel"/>
                </Button>
            </CardActions>

            <ConfirmDelete onClose={handleConfirmationClose} onDeleteClick={handlePaymentDelete} open={openAutoPaymentConfirmation}/>
        </Card>
    );
};

export default ManageAutomaticPayment;
